import type {
  BaseEntity,
  EntityHooks,
  SerializedViewState,
} from "../../../UniversalCRMTable/types";
import type { ConfigWithEnhancedBulkActions } from "@/lib/types/bulk-actions-enhanced";
import type { GroupingOptions } from "@/lib/utils/crm/grouping-strategies/types";
import type {
  FilterState,
  PaginationModeContextValue,
} from "../../contexts/types";
import type { EntityPageQueryData } from "../useEntityPageMemoizedState";
import { useUniversalEntityPageCallbacks } from "../useUniversalEntityPageCallbacks";

interface CompositionCallbacksDataState<T extends BaseEntity> {
  data: EntityPageQueryData<T> | null | undefined;
  isLoading: boolean;
  processedData: T[];
  groupableFields: Array<{
    fieldId: string;
    label: string;
    type: string;
    groupingOptions?: GroupingOptions;
  }>;
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

/**
 * Composition hook for Memoized Callbacks
 * Assembles memoized callbacks for rendering and UI operations
 *
 * Job Duty: Assemble memoized callbacks for rendering and UI
 */
export function useCompositionCallbacks<T extends BaseEntity>(
  entityName: string,
  config: ConfigWithEnhancedBulkActions<T>,
  dataState: CompositionCallbacksDataState<T>,
  filterState: FilterState<T>,
  paginationModeState: PaginationModeContextValue,
  handleEditItem: (item: T) => void,
  handleViewItem: (item: T) => void,
  handleInlineEdit: (item: T) => Promise<void>,
  handlePageChange: (page: number) => void,
  handlePageSizeChange: (size: number) => void,
  handleExportWithScope: (scope: string, format: string) => Promise<void>,
  handleSavedViewsReady: (
    serialize: () => SerializedViewState,
    apply: (filters: SerializedViewState, isManualSelection?: boolean) => void
  ) => void,
  handleOpenDetailModal: (item: T, ids: string[], index: number) => void,
  hooks: EntityHooks<T>,
  _filterHelpers: unknown
) {
  const callbacks = useUniversalEntityPageCallbacks({
    entityName,
    config,
    data: dataState.data,
    isLoading: dataState.isLoading,
    processedData: dataState.processedData,
    searchTerm: filterState.searchTerm,
    setSearchTerm: filterState.setSearchTerm,
    tableFilters: filterState.tableFilters,
    setTableFilters: filterState.setTableFilters,
    currentPage: paginationModeState.paginationMode.page,
    pagination: {
      page: paginationModeState.paginationMode.page,
      pageSize: paginationModeState.paginationMode.pageSize,
      cursor: paginationModeState.paginationMode.cursor,
      totalCount: paginationModeState.paginationMode.totalCount,
      pageInfo: paginationModeState.paginationMode.pageInfo,
    },
    handleEditItem,
    handleViewItem,
    handleInlineEdit,
    handlePageChange,
    handlePageSizeChange,
    handleExportWithScope,
    handleSavedViewsReady,
    handleOpenDetailModal,
    serializeFilters: filterState.serializeFilters,
    applyFilters: filterState.applyFilters,
    hooks,
    groupableFields: dataState.groupableFields,
    // Infinite scroll (Table footer uses Load More; pagination mode uses TablePagination)
    loadMore: dataState.loadMore,
    hasNextPage: dataState.hasNextPage,
    isFetchingNextPage: dataState.isFetchingNextPage,
    infiniteScrollEnabled: paginationModeState.paginationMode.mode === "infinite-scroll",
    autoScrollEnabled: paginationModeState.paginationMode.mode === "infinite-scroll",
  });

  const { handleGroupingExport, renderSimpleTable } = callbacks;

  return {
    callbacks,
    handleGroupingExport,
    renderSimpleTable,
  };
}
